import type { SetState } from "zustand";

import type { FormSuggestion, FormSuggestionState } from "../../types";
import type { AskPerformYardState } from "../types";

import { persistSuggestionOutcome } from "../helpers";

export const createFormSlice = (set: SetState<AskPerformYardState>) => ({
	formSuggestionStates: [] as FormSuggestionState[],
	creatingFormIds: new Set<string>(),

	setFormSuggestions: (messageId: string, suggestions: FormSuggestion[]) => {
		set((state) => {
			const msgIdx = state.messages.findIndex((m) => m.id === messageId);
			const updatedMessages = state.messages.slice();
			if (msgIdx !== -1) {
				updatedMessages[msgIdx] = { ...updatedMessages[msgIdx], formSuggestions: suggestions };
			}

			const newStates: FormSuggestionState[] = suggestions.map((s) => ({
				suggestion: s,
				status: "pending" as const,
				messageId,
			}));

			// Replace any existing states for this messageId; preserve states tied to other messages
			// (created/deleted suggestions on prior messages should keep their status).
			const otherStates = state.formSuggestionStates.filter((fs) => fs.messageId !== messageId);

			return {
				messages: updatedMessages,
				formSuggestionStates: [...otherStates, ...newStates],
			};
		});
	},

	markFormCreated: (suggestionId: string, formId: string, formBaseId: string) => {
		// Durable, not just in-memory — see helpers.ts. Guards against recreating the
		// same form after a reload or in a fresh tab, where in-memory state is gone.
		persistSuggestionOutcome(suggestionId, { kind: "form", createdFormId: formId, createdFormBaseId: formBaseId });
		set((state) => ({
			formSuggestionStates: state.formSuggestionStates.map((fs) =>
				fs.suggestion.id === suggestionId
					? { ...fs, status: "created" as const, createdFormId: formId, createdFormBaseId: formBaseId }
					: fs
			),
		}));
	},

	markFormDeleted: (suggestionId: string) => {
		set((state) => ({
			formSuggestionStates: state.formSuggestionStates.map((fs) =>
				fs.suggestion.id === suggestionId ? { ...fs, status: "deleted" as const } : fs
			),
		}));
	},

	markFormCreating: (suggestionId: string) => {
		set((state) => ({
			creatingFormIds: new Set(state.creatingFormIds).add(suggestionId),
		}));
	},

	clearFormCreating: (suggestionId: string) => {
		set((state) => {
			const next = new Set(state.creatingFormIds);
			next.delete(suggestionId);
			return { creatingFormIds: next };
		});
	},
});
